import React, { useRef, useMemo, useState } from "react";
import { scaleLinear } from "d3-scale";
import { extent } from "d3-array";
import { FormControl, InputLabel } from "@material-ui/core";
import { useChartDimensions } from "./common/utils";
import { Channels } from "../../constants/graphTypes";
import GraphSelect from "../GraphSelect";

const MARGIN = { top: 30, right: 30, bottom: 55, left: 70 };
const ACCEPTED_COLOR = "rgb(0,0,255)";
const EXCLUDED_COLOR = "rgb(255,0,0)";

// Galaxy data uses Ch1_amp, Ch2_amp naming — detect which are present
const detectGalaxyChannels = (graphData) => {
    if (!graphData?.length) return Channels.slice(0, 2);
    return Channels.filter(c => graphData.some(d => d[c.name + "_amp"] != null));
};

const GalaxyGraph = ({ graphData }) => {
    const ref = useRef(null);
    const { width, height } = useChartDimensions(ref);

    const visibleChannels = useMemo(() => detectGalaxyChannels(graphData), [graphData]);
    const defaultX = visibleChannels[1]?.name ?? visibleChannels[0]?.name ?? "Ch2";
    const defaultY = visibleChannels[0]?.name ?? "Ch1";

    const [xChannel, setXChannel] = useState(defaultX);
    const [yChannel, setYChannel] = useState(defaultY);

    const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
    const innerHeight = Math.max(0, (height || 450) - MARGIN.top - MARGIN.bottom);

    const { xScale, yScale } = useMemo(() => {
        if (!graphData?.length) return {};
        const xKey = xChannel + "_amp";
        const yKey = yChannel + "_amp";
        const xScale = scaleLinear()
            .domain(extent(graphData, d => d[xKey]))
            .nice()
            .range([0, innerWidth]);
        const yScale = scaleLinear()
            .domain(extent(graphData, d => d[yKey]))
            .nice()
            .range([innerHeight, 0]);
        return { xScale, yScale };
    }, [graphData, xChannel, yChannel, innerWidth, innerHeight]);

    if (!graphData?.length || !xScale) {
        return <div ref={ref} style={{ height: 450 }}><p>No data</p></div>;
    }

    const xTicks = xScale.ticks(5);
    const yTicks = yScale.ticks(5);
    const xKey = xChannel + "_amp";
    const yKey = yChannel + "_amp";

    return (
        <>
            <div id="channels">
                <FormControl variant="standard" sx={{ m: 1, minWidth: 240 }}>
                    <InputLabel>X</InputLabel>
                    <GraphSelect graph={visibleChannels} defaultValue={xChannel}
                        onSelect={e => setXChannel(e.target.value)} />
                </FormControl>
                <FormControl variant="standard" sx={{ m: 1, minWidth: 240 }}>
                    <InputLabel>Y</InputLabel>
                    <GraphSelect graph={visibleChannels} defaultValue={yChannel}
                        onSelect={e => setYChannel(e.target.value)} />
                </FormControl>
            </div>

            <div ref={ref} style={{ height: 450 }} className="graphborder graphborder-dashed border-cyan-500">
                <svg width={width} height={height || 450}>
                    <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                        {yTicks.map(t => (
                            <line key={t} x1={0} x2={innerWidth} y1={yScale(t)} y2={yScale(t)}
                                stroke="#e5e7eb" strokeWidth={1} />
                        ))}

                        {graphData.map((d, i) => {
                            const cx = xScale(d[xKey]);
                            const cy = yScale(d[yKey]);
                            if (cx == null || cy == null) return null;
                            return (
                                <circle key={i} cx={cx} cy={cy} r={2}
                                    fill={d.qc === "Accepted" ? ACCEPTED_COLOR : EXCLUDED_COLOR}
                                    fillOpacity={0.4} />
                            );
                        })}

                        <g transform={`translate(0,${innerHeight})`}>
                            <line x1={0} x2={innerWidth} stroke="#374151" />
                            {xTicks.map(t => (
                                <g key={t} transform={`translate(${xScale(t)},0)`}>
                                    <line y2={5} stroke="#374151" />
                                    <text y={18} textAnchor="middle" fontSize={10}>{t.toFixed(0)}</text>
                                </g>
                            ))}
                            <text x={innerWidth / 2} y={42} textAnchor="middle" fontSize={11} fill="#374151">
                                {xChannel} Amplitude
                            </text>
                        </g>

                        <g>
                            <line y1={0} y2={innerHeight} stroke="#374151" />
                            {yTicks.map(t => (
                                <g key={t} transform={`translate(0,${yScale(t)})`}>
                                    <line x1={-5} stroke="#374151" />
                                    <text x={-8} dy="0.32em" textAnchor="end" fontSize={10}>{t.toFixed(0)}</text>
                                </g>
                            ))}
                            <text transform={`translate(-52,${innerHeight / 2}) rotate(-90)`}
                                textAnchor="middle" fontSize={11} fill="#374151">
                                {yChannel} Amplitude
                            </text>
                        </g>

                        <g transform={`translate(${innerWidth - 110}, 0)`}>
                            <circle cx={9} cy={6} r={4} fill={ACCEPTED_COLOR} fillOpacity={0.7} />
                            <text x={18} y={10} fontSize={10}>Accepted</text>
                            <circle cx={9} cy={22} r={4} fill={EXCLUDED_COLOR} fillOpacity={0.7} />
                            <text x={18} y={26} fontSize={10}>Excluded</text>
                        </g>
                    </g>
                </svg>
            </div>
        </>
    );
};

export default GalaxyGraph;
